import axios from "axios";
import { useAuthStore } from "@/stores/auth";
import { endRequest, startRequest } from "@/lib/progress";
import { notify } from "@/lib/notify";

// In dev the vite proxy forwards /api to the local worker; in production
// VITE_API_URL points at the deployed api origin.
const api = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL ?? ""}/panel/v1`,
  // httpOnly refresh çerezi farklı origin'e (api.noxussoft.com) gidiyor; bu olmadan tarayıcı
  // çerezi ne gönderir ne de Set-Cookie'yi kabul eder.
  withCredentials: true,
});

// Üstteki çubuk yalnızca yazma isteklerini izliyor. Sayfa geçişlerinde yeni sayfanın verisini
// react-query GET ile çekiyor ve her geçişte çubuk yanıp sönüyordu — listelerin zaten iskeleti var,
// çubuk oraya bir şey katmıyordu. Kaydet/sil/çevir gibi işlemlerin ise ekranda başka bir karşılığı
// yok, çubuk orada duruyor.
const tracksProgress = (config) =>
  (config?.method ?? "get").toLowerCase() !== "get";

api.interceptors.request.use((config) => {
  if (tracksProgress(config)) {
    startRequest();
  }

  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Yenileme döngüsünden muaf uçlar. YALNIZCA bunlar: `/auth/` ön ekiyle eşleştirmek `/auth/me`,
// `/auth/profile` ve `/auth/password`'ü de kapsıyordu. useAccess `/auth/me`'yi 5 dakikalık
// staleTime ile çekiyor, access token 15 dakikalık — süre dolunca o 401 ne yenileme ne çıkış
// tetikliyordu ve panel "bağlantı kurulamadı" ekranında kilitleniyordu.
const NO_REFRESH = /\/auth\/(refresh|logout|login)$/;

const endSessionWithNotice = async () => {
  notify.warning("Oturumun sona erdi, tekrar giriş yapman gerekiyor.");

  const { endSession } = await import("@/lib/session");

  await endSession();
};

// 401 = access token süresi doldu (15 dk) ya da hesap/marka durumu değişti. Çerez hâlâ
// geçerliyse sessizce yenileyip isteği bir kez tekrarlıyoruz; kullanıcı fark etmiyor.
api.interceptors.response.use(
  (response) => {
    if (tracksProgress(response.config)) {
      endRequest();
    }

    return response;
  },
  async (error) => {
    const request = error.config;

    // Her artırmanın tam bir azaltması olmalı; yeniden denenen istek kendi interceptor turunda
    // ayrıca sayılıyor. request yoksa istek hiç kurulamamış, sayaç da hiç artmamış.
    if (request && tracksProgress(request)) {
      endRequest();
    }

    const status = error.response?.status;

    if (status !== 401 || NO_REFRESH.test(request?.url ?? "")) {
      return Promise.reject(error);
    }

    if (request._retried) {
      await endSessionWithNotice();

      return Promise.reject(error);
    }

    // Impersonation'ın kendi refresh çerezi yok; yenileme superadmin'in KENDİ çerezinden
    // olurdu ve kullanıcı arayüz değişmeden sessizce superadmin'e dönerdi.
    if (useAuthStore.getState().impersonation) {
      notify.warning("Kiracı oturumun sona erdi, listeye dönüldü.");
      useAuthStore.getState().stopImpersonation();

      return Promise.reject(error);
    }

    const { refreshSession } = await import("@/lib/session");
    const token = await refreshSession();

    if (!token) {
      await endSessionWithNotice();

      return Promise.reject(error);
    }

    request._retried = true;
    request.headers.Authorization = `Bearer ${token}`;

    return api(request);
  },
);

export default api;
